import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Paper, Typography, Tabs, Tab, TextField, MenuItem, Grid,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Chip,
  Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert, Avatar, TablePagination,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon, Cancel as RejectIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { approvalsAPI } from '../../services/api';
import { nameInitial, textFieldSx } from '../components/reportUi';

const FORM_TYPE_TABS = [
  { key: 'hbnc', label: 'HBNC' }, { key: 'hbyc', label: 'HBYC' },
  { key: 'nrc', label: 'NRC' }, { key: 'sncu', label: 'SNCU' },
];

// Scoped per form type — visit_3 means day 3 for HBNC but month 3 for
// HBYC/SNCU; the active tab disambiguates, a flat map would not.
const VISIT_LABELS = {
  hbnc: { visit_1: 'दिवस १', visit_3: 'दिवस ३', visit_7: 'दिवस ७', visit_14: 'दिवस १४', visit_21: 'दिवस २१', visit_28: 'दिवस २८', visit_42: 'दिवस ४२' },
  hbyc: { visit_3: 'महिना ३', visit_6: 'महिना ६', visit_9: 'महिना ९', visit_12: 'महिना १२', visit_15: 'महिना १५' },
  nrc: { visit_15days: '१५ दिवस', visit_1month: '१ महिना', visit_2months: '२ महिने', visit_3months: '३ महिने', visit_6months: '६ महिने' },
  sncu: { visit_3: 'महिना ३', visit_6: 'महिना ६', visit_9: 'महिना ९', visit_12: 'महिना १२' },
};

function StatusChip({ status }) {
  const map = {
    pending: { label: 'प्रलंबित', color: 'warning', variant: 'outlined' },
    approved: { label: 'मंजूर', color: 'success', variant: 'filled' },
    rejected: { label: 'नाकारले', color: 'error', variant: 'filled' },
  };
  const cfg = map[status] || map.pending;
  return <Chip label={cfg.label} color={cfg.color} variant={cfg.variant} size="small" sx={{ fontWeight: 600 }} />;
}

function RejectDialog({ open, item, onClose, onConfirm, submitting }) {
  const [remarks, setRemarks] = useState('');
  useEffect(() => { if (open) setRemarks(''); }, [open]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>भेट नाकारा</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2, color: 'grey.600', fontSize: '0.875rem' }}>
          {item?.subjectName} — {VISIT_LABELS[item?.formType]?.[item?.visitKey] || item?.visitKey}
        </Typography>
        <TextField
          fullWidth multiline minRows={3} autoFocus
          label="नाकारण्याचे कारण (आवश्यक)"
          value={remarks} onChange={(e) => setRemarks(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>रद्द करा</Button>
        <Button
          variant="contained" color="error" disabled={!remarks.trim() || submitting}
          onClick={() => onConfirm(remarks.trim())}
        >
          नाकारा
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Approvals() {
  const navigate = useNavigate();
  const location = useLocation();
  // This page is dual-routed at /admin/approvals (ADMIN/TALUKA_ADMIN) and
  // /admin/supervisor/approvals (SUPERVISOR) — AdminLayout hard-redirects a
  // SUPERVISOR away from anything outside /admin/supervisor/*, so the "open
  // full form" link must stay under that same prefix for them, or it bounces
  // straight back to the dashboard instead of opening.
  const formDetailBase = location.pathname.startsWith('/admin/taluka-admin')
    ? '/admin/taluka-admin'
    : location.pathname.startsWith('/admin/supervisor')
    ? '/admin/supervisor'
    : '/admin';
  const [formType, setFormType] = useState('hbnc');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actioningKey, setActioningKey] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);

  const fetchItems = useCallback(() => {
    setLoading(true);
    setError('');
    approvalsAPI.listVisits({
      form_type: formType, status: statusFilter,
      search: search || undefined, skip: page * rowsPerPage, limit: rowsPerPage,
    })
      .then((res) => { setItems(res.data.items); setTotal(res.data.total); })
      .catch(() => setError('यादी लोड करताना त्रुटी आली'))
      .finally(() => setLoading(false));
  }, [formType, statusFilter, search, page, rowsPerPage]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { setPage(0); }, [formType, statusFilter, search]);

  const keyOf = (i) => `${i.formType}:${i.formId}:${i.visitKey}`;

  const handleApprove = async (item) => {
    setActioningKey(keyOf(item));
    try {
      await approvalsAPI.approveVisit(item.formType, item.formId, item.visitKey, {});
      fetchItems();
    } catch { setError('मंजुरी देताना त्रुटी आली'); }
    finally { setActioningKey(null); }
  };

  const handleReject = async (remarks) => {
    const item = rejectTarget;
    setActioningKey(keyOf(item));
    try {
      await approvalsAPI.rejectVisit(item.formType, item.formId, item.visitKey, { remarks });
      setRejectTarget(null);
      fetchItems();
    } catch { setError('नाकारताना त्रुटी आली'); }
    finally { setActioningKey(null); }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: 'grey.900' }}>भेटींची मंजुरी</Typography>
        <Typography sx={{ fontSize: '0.875rem', color: 'grey.600', mt: 0.25 }}>
          प्रत्येक आशा कार्यकर्तीने सादर केलेल्या वैयक्तिक भेटीला मंजुरी द्या किंवा नाकारा
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Tabs value={formType} onChange={(e, v) => setFormType(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'grey.200' }}>
        {FORM_TYPE_TABS.map((t) => <Tab key={t.key} value={t.key} label={t.label} sx={{ fontWeight: 700, textTransform: 'none' }} />)}
      </Tabs>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Grid container spacing={1.5}>
          <Grid item xs={6} sm={3}>
            <TextField select fullWidth size="small" label="स्थिती" value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)} sx={textFieldSx}>
              <MenuItem value="pending">प्रलंबित</MenuItem>
              <MenuItem value="approved">मंजूर</MenuItem>
              <MenuItem value="rejected">नाकारले</MenuItem>
              <MenuItem value="all">सर्व</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField fullWidth size="small" label="माता/बालक किंवा ABHA शोधा"
              value={search} onChange={(e) => setSearch(e.target.value)} sx={textFieldSx} />
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: 'grey.50', fontWeight: 700, fontSize: '0.78rem' } }}>
                <TableCell>आशा कार्यकर्ता</TableCell>
                <TableCell>माता / बालक</TableCell>
                <TableCell>भेट</TableCell>
                <TableCell>भेट दिनांक</TableCell>
                <TableCell>सादर केले</TableCell>
                <TableCell>स्थिती</TableCell>
                <TableCell align="right">कृती</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5, color: 'grey.500' }}>कोणतीही नोंद सापडली नाही</TableCell></TableRow>
              )}
              {items.map((item) => {
                const busy = actioningKey === keyOf(item);
                return (
                  <TableRow key={keyOf(item)} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: '0.8rem', bgcolor: 'primary.lighter', color: 'primary.main' }}>
                          {nameInitial(item.ashaWorkerName)}
                        </Avatar>
                        {item.ashaWorkerName || '-'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {item.subjectName}
                      {item.motherAbha && <Typography sx={{ fontSize: '0.72rem', color: 'grey.500' }}>{item.motherAbha}</Typography>}
                    </TableCell>
                    <TableCell>{VISIT_LABELS[item.formType]?.[item.visitKey] || item.visitKey}</TableCell>
                    <TableCell>{item.visitDate || '-'}</TableCell>
                    <TableCell>{item.completedAt ? new Date(item.completedAt).toLocaleDateString('en-IN') : '-'}</TableCell>
                    <TableCell><StatusChip status={item.approvalStatus} /></TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => navigate(`${formDetailBase}/forms/${item.formType}/${item.formId}`)} title="संपूर्ण फॉर्म पहा">
                        <OpenIcon fontSize="small" />
                      </IconButton>
                      <Button size="small" color="success" startIcon={<ApproveIcon fontSize="small" />} disabled={busy}
                        onClick={() => handleApprove(item)} sx={{ textTransform: 'none' }}>
                        मंजूर करा
                      </Button>
                      <Button size="small" color="error" startIcon={<RejectIcon fontSize="small" />} disabled={busy}
                        onClick={() => setRejectTarget(item)} sx={{ textTransform: 'none' }}>
                        नाकारा
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div" count={total} page={page} onPageChange={(e, p) => setPage(p)}
            rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </TableContainer>
      )}

      <RejectDialog
        open={!!rejectTarget} item={rejectTarget}
        onClose={() => setRejectTarget(null)} onConfirm={handleReject}
        submitting={!!actioningKey}
      />
    </Container>
  );
}

export default Approvals;
